import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useApi } from "../../hooks/useApi";
import {
  EVIDENCE_POLICY_LABELS,
  EvidencePolicy,
  EvidencePolicySlot,
  EvidencePolicySource,
  StorefrontSummary,
} from "../../types/evidence";
import { URL_MESSAGE, urlMessage } from "../../utils/storefrontFields";

dayjs.extend(utc);

type PolicyDraft = { body: string; url: string };

// Mirrors policySource in the API: the Shopify copy prints by default once a seed exists.
function policySource(policy: EvidencePolicy | undefined): EvidencePolicySource {
  return policy?.source ?? (policy?.shopify ? "shopify" : "manual");
}

function policyStatus(policy: EvidencePolicy | undefined, required: boolean): string {
  const source = policySource(policy);
  const text = source === "shopify" ? policy?.shopify : policy?.manual;
  if (!text?.body) return required ? 'Missing. Dossiers get a "policy missing" note.' : "Not needed: a digital storefront ships nothing.";
  // Only a digital storefront's shipping slot is not required, and the dossier leaves it out.
  const printed = !required ? "Not printed for digital products" : source === "shopify" ? "Prints the Shopify text" : "Prints your text";
  if (!text.syncedAt) return `${printed}.`;
  return `${printed}, ${source === "shopify" ? "synced" : "updated"} ${dayjs.utc(text.syncedAt).format("D MMM")}.`;
}

export default function StorefrontPolicySlot({
  slot,
  storefrontId,
  policy,
  required,
  open,
  onToggle,
  switching,
  onSelectSource,
  onChange,
  onError,
  onSaved,
}: {
  slot: EvidencePolicySlot;
  storefrontId: number;
  policy: EvidencePolicy | undefined;
  required: boolean;
  open: boolean;
  onToggle: () => void;
  switching: boolean;
  onSelectSource: (source: EvidencePolicySource) => void;
  onChange: (next: StorefrontSummary) => void;
  onError: (message: string | null) => void;
  onSaved?: (message: string) => void;
}) {
  const { put } = useApi();

  const label = EVIDENCE_POLICY_LABELS[slot];
  const saved = policy?.manual;

  const [draft, setDraft] = useState<PolicyDraft>(() => ({ body: saved?.body ?? "", url: saved?.url ?? "" }));
  const [saving, setSaving] = useState(false);
  // A 400 the API answers about the URL reads under that field, not in the alert above.
  const [rejected, setRejected] = useState<string | null>(null);

  // Reseeding on every row rewrite would discard whatever is half-typed, so an edited draft stays.
  const seeded = useRef({ body: saved?.body ?? "", url: saved?.url ?? "" });
  useEffect(() => {
    const before = seeded.current;
    const next = { body: saved?.body ?? "", url: saved?.url ?? "" };
    if (before.body === next.body && before.url === next.url) return;
    seeded.current = next;
    setDraft((current) => (current.body === before.body && current.url === before.url ? next : current));
  }, [saved?.body, saved?.url]);

  const savePolicy = async () => {
    const url = draft.url.trim();
    const invalidUrl = urlMessage(url);
    if (invalidUrl) {
      setRejected(invalidUrl);
      return;
    }
    setSaving(true);
    try {
      const { storefront: next } = await put<{ storefront: StorefrontSummary }>(
        `/api/storefronts/${storefrontId}`,
        { policies: { [slot]: { body: draft.body, url: url || null } } },
      );
      onChange(next);
      const storedText = next.policies?.[slot]?.manual;
      if (storedText) setDraft({ body: storedText.body, url: storedText.url ?? "" });
      onError(null);
      setRejected(null);
      onSaved?.(`${label} saved`);
    } catch (err: any) {
      if (err?.status === 400 && url) setRejected(URL_MESSAGE);
      else onError(err?.message || `Failed to save the ${label.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const dirty = draft.body !== (saved?.body ?? "") || draft.url !== (saved?.url ?? "");
  const urlFault = urlMessage(draft.url) ?? rejected;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 3, py: 1.5 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="caption" color="text.secondary">{policyStatus(policy, required)}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {policy?.shopify && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={policySource(policy)}
              aria-label={`${label} source`}
              disabled={switching}
              onChange={(_, source: EvidencePolicySource | null) => {
                if (source && source !== policySource(policy)) onSelectSource(source);
              }}
            >
              <ToggleButton value="shopify" sx={{ px: 1.5, py: 0.25 }}>Shopify</ToggleButton>
              {/* Selecting a slot with no pasted text would print nothing at all. */}
              <ToggleButton value="manual" disabled={!policy.manual?.body} sx={{ px: 1.5, py: 0.25 }}>Manual</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button size="small" aria-label={`${open ? "Close" : "Edit"} ${label}`} onClick={onToggle}>
            {open ? "Close" : "Edit"}
          </Button>
        </Stack>
      </Box>
      {open && (
        <Stack spacing={1.5} sx={{ pb: 2 }}>
          {policy?.shopify && (
            <TextField
              label="Shopify text"
              value={policy.shopify.body}
              multiline
              maxRows={6}
              size="small"
              fullWidth
              slotProps={{ input: { readOnly: true } }}
              helperText="Rewritten by every seed. Your text below is never touched by a seed; saving it selects it."
            />
          )}
          <TextField
            label="Policy URL"
            value={draft.url}
            onChange={(event) => { setDraft((current) => ({ ...current, url: event.target.value })); setRejected(null); }}
            disabled={saving}
            size="small"
            fullWidth
            error={urlFault !== null}
            helperText={urlFault ?? undefined}
          />
          <TextField
            label="Policy text"
            value={draft.body}
            onChange={(event) => setDraft((current) => ({ ...current, body: event.target.value }))}
            multiline
            minRows={6}
            disabled={saving}
            size="small"
            fullWidth
            // The API takes no empty body, so emptying the field here cannot remove the pasted copy.
            helperText={saved?.body && !draft.body.trim()
              ? "Paste the policy text; a slot cannot be emptied here"
              : undefined}
          />
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {`${draft.body.length.toLocaleString()} characters`}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {dirty && (
                <Button
                  size="small"
                  color="inherit"
                  disabled={saving}
                  onClick={() => setDraft({ body: saved?.body ?? "", url: saved?.url ?? "" })}
                  sx={{ color: "text.secondary" }}
                >
                  Discard changes
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                disabled={saving || !draft.body.trim() || urlMessage(draft.url) !== null}
                startIcon={saving ? <CircularProgress size={14} /> : null}
                onClick={savePolicy}
              >
                Save policy
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}
    </>
  );
}
